import './Admin.scss';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminStaffManage from './staff/AdminStaffManage';
import AdminAttendance from './staff/AdminAttendance';
import TopbarAttendance from './staff/TopbarAttendance';
import AdminTodoManage from './todos/AdminTodoManage';
import FinanceDashboard from './finance/FinanceDashboard';
import PayrollSection from './payroll/PayrollSection';
import AdminDocumentManage from './documents/AdminDocumentManage';
import AdminInquiryManage from './inquiry/AdminInquiryManage';
import AdminContractManage from './contract/AdminContractManage';
import { clearAdminSession, getAdminSession, isSuperAdmin } from './adminSession';

type MenuKey =
  | 'admintodos'
  | 'registerrecruit'
  | 'recruitlistmanagepre'
  | 'worshipmanage'
  | 'emailmanage'
  | 'pushnotifi'
  | 'serviceapply'
  | 'servicedetail'
  | 'noticepost'
  | 'boardpostmanage'
  | 'retreatmanage'
  | 'adminuser'
  | 'adminstaff'
  | 'attendanceadmin'
  | 'finance'
  | 'payroll'
  | 'documents'
  | 'inquiry'
  | 'contracts'
  | 'adminmanage'
  | 'backup';

type MenuGroupKey = 'service-management' | 'board-management' | 'owner-only';

type MenuItem = {
  key: MenuKey;
  label: string;
  adminOnly?: boolean;
  superOnly?: boolean;
  icon: string;
};

type MenuGroup = {
  key: MenuGroupKey;
  label: string;
  icon: string;
  childKeys: MenuKey[];
  bottom?: boolean;
};

const MENU_ITEMS: MenuItem[] = [
  { key: 'admintodos', label: 'To-Do 관리', icon: '✅' },
  { key: 'attendanceadmin', label: '출퇴근 현황', icon: '📅' },
  { key: 'adminstaff', label: '직원 관리', icon: '🛡️', superOnly: true },
  { key: 'finance', label: '재무 관리', icon: '💰' },
  { key: 'payroll', label: '급여관리', icon: '🧾', adminOnly: true },
  { key: 'documents', label: '문서관리', icon: '📁' },
  { key: 'inquiry', label: '문의 접수', icon: '📨' },
  { key: 'contracts', label: '온라인 계약', icon: '📝' },
];

const MENU_GROUPS: MenuGroup[] = [
  {
    key: 'service-management',
    label: '서비스 관리',
    icon: '🧩',
    childKeys: ['serviceapply', 'servicedetail'],
  },
  {
    key: 'board-management',
    label: '게시판 관리',
    icon: '📝',
    childKeys: ['noticepost', 'boardpostmanage'],
  },
  {
    key: 'owner-only',
    label: '관리자 전용',
    icon: '🔒',
    childKeys: ['adminstaff', 'payroll', 'backup'],
    bottom: true,
  },
];

function canViewMenuItem(item: MenuItem, session: ReturnType<typeof getAdminSession>): boolean {
  if (item.superOnly) return isSuperAdmin(session);
  if (item.adminOnly) return Boolean(session);
  return true;
}

function AdminMainContent({
  activeMenu,
  adminSession,
}: {
  activeMenu: MenuKey;
  adminSession: ReturnType<typeof getAdminSession>;
}) {
  if (activeMenu === 'adminstaff' && !isSuperAdmin(adminSession)) {
    return (
      <p className="admin-main-layout__forbidden">관리자 승인 메뉴는 최종관리자만 이용할 수 있습니다.</p>
    );
  }

  if (activeMenu === 'payroll' && !adminSession) {
    return (
      <p className="admin-main-layout__forbidden">급여관리 메뉴는 관리자만 이용할 수 있습니다.</p>
    );
  }

  switch (activeMenu) {
    case 'admintodos':
      return <AdminTodoManage />;
    case 'attendanceadmin':
      return <AdminAttendance />;
    case 'finance':
      return <FinanceDashboard />;
    case 'payroll':
      return <PayrollSection />;
    case 'documents':
      return <AdminDocumentManage />;
    case 'inquiry':
      return <AdminInquiryManage />;
    case 'contracts':
      return <AdminContractManage />;
    case 'adminstaff':
      return <AdminStaffManage />;
    default:
      return <AdminTodoManage />;
  }
}

export default function Main(props: any) {
  const navigate = useNavigate();
  const adminSession = getAdminSession();
  const userData = adminSession?.email ?? sessionStorage.getItem('user');
  const superAdmin = isSuperAdmin(adminSession);
  const [activeMenu, setActiveMenu] = useState<MenuKey>('admintodos');
  const [openMenuGroups, setOpenMenuGroups] = useState<Record<MenuGroupKey, boolean>>({
    'service-management': false,
    'board-management': false,
    'owner-only': false,
  });
  const contentScrollRef = useRef<HTMLElement>(null);

  const scrollContentToTop = () => {
    contentScrollRef.current?.scrollTo({ top: 0, behavior: 'auto' });
  };

  const visibleMenus = useMemo(
    () => MENU_ITEMS.filter((item) => canViewMenuItem(item, adminSession)),
    [adminSession]
  );

  useEffect(() => {
    const activeMenuVisible = visibleMenus.some((menu) => menu.key === activeMenu);
    if (!activeMenuVisible && visibleMenus.length > 0) {
      setActiveMenu(visibleMenus[0].key);
    }
  }, [activeMenu, visibleMenus]);

  useEffect(() => {
    const parentGroup = MENU_GROUPS.find((group) => group.childKeys.includes(activeMenu));
    if (!parentGroup) return;
    setOpenMenuGroups((prev) => ({ ...prev, [parentGroup.key]: true }));
  }, [activeMenu]);

  const visibleMenuMap = useMemo(
    () => new Map(visibleMenus.map((menu) => [menu.key, menu] as const)),
    [visibleMenus]
  );

  const groupedChildKeys = useMemo(
    () => new Set(MENU_GROUPS.flatMap((group) => group.childKeys)),
    []
  );

  const firstChildGroupMap = useMemo(() => {
    const map = new Map<MenuKey, MenuGroup>();
    MENU_GROUPS.forEach((group) => {
      const firstVisibleChild = group.childKeys.find((key) => visibleMenuMap.has(key));
      if (firstVisibleChild) map.set(firstVisibleChild, group);
    });
    return map;
  }, [visibleMenuMap]);

  const toggleMenuGroup = (groupKey: MenuGroupKey) => {
    setOpenMenuGroups((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  const normalVisibleMenus = useMemo(
    () =>
      visibleMenus.filter(
        (menu) =>
          !MENU_GROUPS.some((group) => group.bottom && group.childKeys.includes(menu.key)),
      ),
    [visibleMenus],
  );

  const ownerOnlyGroups = useMemo(() => MENU_GROUPS.filter((group) => group.bottom), []);

  const renderMenuGroup = (group: MenuGroup) => {
    const childMenus = group.childKeys
      .map((key) => visibleMenuMap.get(key))
      .filter((item): item is MenuItem => Boolean(item));
    if (childMenus.length === 0) return null;

    const isOpen = openMenuGroups[group.key];
    const isGroupActive = childMenus.some((child) => child.key === activeMenu);

    return (
      <div
        key={group.key}
        className={`admin-main-layout__menu-group${
          group.bottom ? ' admin-main-layout__menu-group--owner' : ''
        }`}
      >
        <button
          type="button"
          className={`admin-main-layout__menu-btn admin-main-layout__menu-parent${
            isGroupActive ? ' is-active' : ''
          }`}
          onClick={() => toggleMenuGroup(group.key)}
        >
          <span className="admin-main-layout__menu-icon" aria-hidden>
            {group.icon}
          </span>
          <span>{group.label}</span>
          <span className={`admin-main-layout__menu-caret${isOpen ? ' is-open' : ''}`}>▾</span>
        </button>
        {isOpen && (
          <div className="admin-main-layout__submenu">
            {childMenus.map((childMenu) => (
              <button
                key={childMenu.key}
                type="button"
                className={`admin-main-layout__menu-btn admin-main-layout__submenu-btn${
                  activeMenu === childMenu.key ? ' is-active' : ''
                }`}
                onClick={() => {
                  scrollContentToTop();
                  setActiveMenu(childMenu.key);
                }}
              >
                <span>{childMenu.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const handleLogout = () => {
    clearAdminSession();
    navigate('/admin');
    window.scrollTo(0, 0);
  };

  return (
    <div className="AdminContainer admin-main-shell">
      <header className="admin-main-shell__topbar">
        <div className="admin-main-shell__brand">예플랫 관리자 페이지</div>
        <div className="admin-main-shell__user">
          <TopbarAttendance />
          <button
            type="button"
            className="admin-main-shell__home-btn"
            onClick={() => navigate('/')}
          >
            홈
          </button>
          <button
            type="button"
            className="admin-main-shell__home-btn admin-main-shell__logout-btn"
            onClick={handleLogout}
          >
            로그아웃
          </button>
          <span className="admin-main-shell__user-name">
            {adminSession?.name || userData || 'admin'}
          </span>
          <span className="admin-main-shell__user-role">
            {superAdmin ? '최종관리자' : '관리자'}
          </span>
        </div>
      </header>

      <div className="admin-main-layout">
        <aside className="admin-main-layout__sidebar">
          <div className="admin-main-layout__sidebar-title">관리자 메뉴</div>
          <nav className="admin-main-layout__menu admin-main-layout__menu--main">
            {normalVisibleMenus.map((menu) => {
              const group = firstChildGroupMap.get(menu.key);
              if (group && !group.bottom) {
                return renderMenuGroup(group);
              }

              if (groupedChildKeys.has(menu.key)) return null;

              return (
                <button
                  key={menu.key}
                  type="button"
                  className={`admin-main-layout__menu-btn${activeMenu === menu.key ? ' is-active' : ''}`}
                  onClick={() => {
                    scrollContentToTop();
                    setActiveMenu(menu.key);
                  }}
                >
                  <span className="admin-main-layout__menu-icon" aria-hidden>{menu.icon}</span>
                  <span>{menu.label}</span>
                </button>
              );
            })}
            {ownerOnlyGroups.map((group) => renderMenuGroup(group))}
          </nav>
        </aside>

        <section className="admin-main-layout__content" ref={contentScrollRef}>
          <div className="admin-main-layout__content-body">
            <AdminMainContent activeMenu={activeMenu} adminSession={adminSession} />
          </div>
        </section>
      </div>
    </div>
  );
}
